const STORAGE_PREFIX = 'go-to-page-usage:';
const MAX_ENTRIES = 40;
const RECENT_LIMIT = 5;
const FREQUENT_LIMIT = 5;

export interface PageVisit {
  href: string;
  count: number;
  lastUsed: number;
}

const storageKey = (userId: string) => `${STORAGE_PREFIX}${userId}`;

const isPageVisit = (entry: unknown): entry is PageVisit => {
  if (!entry || typeof entry !== 'object') return false;
  const visit = entry as PageVisit;
  return typeof visit.href === 'string' && typeof visit.count === 'number' && typeof visit.lastUsed === 'number';
};

const readStored = (userId: string): PageVisit[] => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey(userId)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isPageVisit) : [];
  } catch {
    return [];
  }
};

const writeStored = (userId: string, visits: PageVisit[]) => {
  window.localStorage.setItem(storageKey(userId), JSON.stringify(visits));
};

const capVisits = (visits: PageVisit[]) => [...visits].sort((left, right) => right.lastUsed - left.lastUsed).slice(0, MAX_ENTRIES);

export function readPageUsage(userId: string): PageVisit[] {
  return readStored(userId);
}

export function recordPageVisit(userId: string, href: string): PageVisit[] {
  const now = Date.now();
  const current = readStored(userId);
  const exists = current.some((entry) => entry.href === href);
  const next = exists ? current.map((entry) => (entry.href === href ? { ...entry, count: entry.count + 1, lastUsed: now } : entry)) : [...current, { href, count: 1, lastUsed: now }];
  const capped = capVisits(next);
  writeStored(userId, capped);
  return capped;
}

export function prunePageUsage(userId: string, allowedHrefs: Set<string>, visits: PageVisit[]): PageVisit[] {
  const pruned = visits.filter((entry) => allowedHrefs.has(entry.href));
  if (pruned.length === visits.length) return visits;
  writeStored(userId, pruned);
  return pruned;
}

export function splitByUsage<T extends { href: string }>(destinations: T[], usage: PageVisit[]): { pinned: T[]; rest: T[] } {
  const byHref = new Map(destinations.map((destination) => [destination.href, destination]));
  const allowed = usage.filter((entry) => byHref.has(entry.href));
  const recentHrefs = [...allowed]
    .sort((left, right) => right.lastUsed - left.lastUsed)
    .slice(0, RECENT_LIMIT)
    .map((entry) => entry.href);
  const recent = new Set(recentHrefs);
  const frequentHrefs = [...allowed]
    .filter((entry) => !recent.has(entry.href))
    .sort((left, right) => right.count - left.count || right.lastUsed - left.lastUsed)
    .slice(0, FREQUENT_LIMIT)
    .map((entry) => entry.href);
  const pinned = [...recentHrefs, ...frequentHrefs].flatMap((href) => {
    const destination = byHref.get(href);
    return destination ? [destination] : [];
  });
  const pinnedHrefs = new Set(pinned.map((destination) => destination.href));
  return {
    pinned,
    rest: destinations.filter((destination) => !pinnedHrefs.has(destination.href)),
  };
}
